import React, { useState, useEffect } from "react";
import '../../styles/main.css';
import { API_BASE_URL } from '../../config/api';

// Update the interface to match your MongoDB structure
interface Release {
  _id: string;
  model: string;
  version: string;
  type: string;
  bins: Array<{
    path: string;
    offset: number;
  }>;
  enabled: boolean;
  internalOnly: boolean;
}

interface ReleasesResponse {
  releases: Release[];
}

const DownloadView: React.FC = () => {
    
  const [data, setData] = useState<ReleasesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>("null");
  const [selectedFirmware, setSelectedFirmware] = useState<string>("null");
  const [loading, setLoading] = useState<boolean>(true);
  
  // Fetch the releases data
  useEffect(() => {
    const fetchReleases = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/releases/public/all`);
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const jsonData: ReleasesResponse = await response.json();
        setData(jsonData);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReleases();
  }, [API_BASE_URL]);

  // Get available products from the releases
  const getAvailableProducts = () => {
    if (!data || !data.releases) return [];
    
    // Get unique model names
    const models = [...new Set(data.releases.map(release => release.model))];
    return models.sort();
  };

  // Handle product selection change
  const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const productName = e.target.value;
    setSelectedProduct(productName);
    setSelectedFirmware("null"); // Reset firmware selection
  };

  // Handle firmware version selection change
  const handleFirmwareChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const firmwareVersion = e.target.value;
    setSelectedFirmware(firmwareVersion);
  };

  // Get available firmware versions for selected product
  const getAvailableFirmwareVersions = () => {
    if (!data || !data.releases || selectedProduct === "null") return [];
    
    // Get releases for the selected product, filtered for production releases
    const productReleases = data.releases
      .filter(release => release.model === selectedProduct && release.type === 'prod')
      .sort((a, b) => {
        // Sort by version in reverse order (newest first)
        return b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' });
      });
    
    return productReleases.map(release => release.version);
  };

  // Get download URL for selected firmware
  const getDownloadUrl = () => {
    if (!data || !data.releases || selectedProduct === "null" || selectedFirmware === "null") {
      return null;
    }
    
    const firmwareInfo = data.releases.find(release => 
      release.model === selectedProduct && 
      release.version === selectedFirmware
    );
    
    if (firmwareInfo && firmwareInfo.bins) {
      // Look for the bin that matches the pattern "ModelName-vVersion.bin"
      const binFile = firmwareInfo.bins.find(bin => {
        const expectedPattern = `${selectedProduct.replace(' ', '_')}-v${selectedFirmware}.bin`;
        return bin.path.toLowerCase().endsWith(expectedPattern.toLowerCase());
      });
      
      if (binFile) {
        return binFile.path;
      }
    }
    
    console.log('No matching binary found');
    return null;
  };

  const getDownloadFilename = () => {
    const downloadUrl = getDownloadUrl();
    if (!downloadUrl) return undefined;
    return downloadUrl.split('/').pop()?.split('?')[0];
  };

  if (error) {
    return <div className="alert alert-danger">Error: {error}</div>;
  }

  if (loading) {
    return (
      <div className="container-fluid py-3">
        <div className="responsive-page-header mb-4">
          <h3 className="mb-1">Firmware Download</h3>
          <p className="text-muted mb-0">Download a firmware binary for manual installation.</p>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading firmware data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="responsive-page-header mb-4">
        <h3 className="mb-1">Firmware Download</h3>
        <p className="text-muted mb-0">Download a firmware binary for manual installation.</p>
      </div>

      {error && <div className="alert alert-danger">Error: {error}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading firmware data...</p>
        </div>
      ) : (
        <div className="row g-4">
          <div className="col-lg-5 col-xl-4">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white">
                <h5 className="mb-0">Select Firmware</h5>
              </div>
              <div className="card-body p-4">
                <div className="mb-4">
                  <label htmlFor="product_model" className="form-label fw-bold">Model</label>
                  <select
                    id="product_model"
                    className="form-select"
                    onChange={handleProductChange}
                    value={selectedProduct === "null" ? "" : selectedProduct}
                  >
                    <option value="null">Choose Your Device...</option>
                    {getAvailableProducts().map((product, index) => (
                      <option key={index} value={product}>
                        {product}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="firmware_version" className="form-label fw-bold">Firmware Version</label>
                  <select
                    id="firmware_version"
                    className="form-select"
                    onChange={handleFirmwareChange}
                    value={selectedFirmware === "null" ? "" : selectedFirmware}
                    disabled={selectedProduct === "null"}
                  >
                    <option value="null">Choose A Firmware Version...</option>
                    {getAvailableFirmwareVersions().map((version, index) => (
                      <option key={index} value={version}>
                        {version}
                      </option>
                    ))}
                  </select>
                </div>

                <a
                  href={getDownloadUrl() || '#'}
                  className={`btn btn-primary btn-lg ${!getDownloadUrl() ? 'disabled' : ''}`}
                  download={getDownloadFilename()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Firmware
                </a>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white">
                <h5 className="mb-0">About Firmware Downloads</h5>
              </div>
              <div className="card-body">
                <p className="mb-2">
                  Use this page when you need to download the firmware binary directly instead of updating through the USB web flasher.
                </p>
                <p className="text-muted mb-0">
                  For most updates, use the guided USB update page from the sidebar.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadView;